import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { Field, Modal, Notice } from '@/components/Modal';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 개선사항 요청. "이게 불편하다 / 이게 안 된다" 를 받는 **하나의 창구**다.
 *
 * 예전에는 "오류사항 신고" 였고 서버가 관리자 메일로 쏘고 끝이었다 — 어디에도 남지 않아 처리 여부를
 * 확인할 수 없었다. 오류와 개선을 나누지 않는 이유는 받는 쪽이 하는 일이 같기 때문이다(DOC-22).
 *
 * ## 창구가 둘이었다 — 하나로 합쳤다 (사용자 지시 2026-09-01)
 *
 * 여기서 낸 것은 `sys_improvement_requests` 에 쌓이고, 사이드바 「개선요청」 화면은
 * `board_request_posts` 를 읽었다. **이름이 둘 다 「개선요청」인데 표가 달라서**, 여기로 낸 사람은
 * 그 화면 목록에서 자기 글을 찾지 못했다. 담당자도 두 곳을 봐야 했다.
 *
 * 이제 <b>어디서 쓰든 요청게시판(`board_request_posts`)으로 들어간다</b> — AI 대화도 같은 곳이다.
 * 반영여부·소요기간을 담당자가 답하는 그 목록이 정본이다.
 *
 * ## 화면 경로는 본문에 붙인다
 *
 * 요청게시판 표에는 `pagePath` 칸이 없다. 그렇다고 버리면 <b>어느 화면이 불편했는지</b>가 사라지는데,
 * 그건 이 창구가 받는 것의 절반이다. 그래서 본문 끝에 한 줄로 적어 함께 남긴다 — 칸을 늘리는 것보다
 * 담당자가 읽기에도 낫다.
 *
 * v1 은 Navigation 안에 인라인으로 들어 있어 사이드바 컴포넌트를 777줄로 만든 원인 중 하나였다.
 * 요청 폼은 사이드바와 아무 관계가 없으므로 분리한다.
 */

/**
 * 요청게시판이 받는 것. 표에 있는 칸은 이 둘뿐이다(`RequestPostRequest`).
 *
 * 부서 칸도 없앴다(사용자 지시 2026-10-06) — 익명으로 올라가는 글이라 소속도 받지 않는다.
 * 서버 쪽 `requesterName` 은 `@NotBlank` 가 아니므로 비워 보낸다.
 */
type RequestPostInput = {
  requesterName: null;
  requestContent: string;
};

export function ImprovementRequestModal({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');

  const submit = useMutation({
    mutationFn: (input: RequestPostInput) =>
      apiFetch<unknown>('/v1/request-posts', { method: 'POST', body: input }),
    onSuccess: () => {
      // 방금 낸 요청이 홈 위젯에 바로 보여야 "접수됐다" 가 눈에 보인다.
      void queryClient.invalidateQueries({ queryKey: queryKeys.requestBoardPosts });
      onClose();
    },
  });

  const canSubmit = Boolean(content.trim()) && !submit.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    submit.mutate({
      // 이름도 부서도 받지 않는다(사용자 지시 2026-09-02 · 2026-10-06).
      // 되물을 길은 `createdBy` 로 남는다.
      requesterName: null,
      requestContent: `${content.trim()}

(화면: ${pathname})`,
    });
  };

  return (
    <Modal
      title="개선사항 요청"
      maxWidth="560px"
      onClose={onClose}
      closeDisabled={submit.isPending}
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={submit.isPending}>
            취소
          </button>
          <button onClick={handleSubmit} className="btn-primary" disabled={!canSubmit}>
            {submit.isPending ? '전송 중...' : '제출'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
        불편한 점과 오류를 함께 받습니다. 현재 페이지 경로와 함께 접수되며, 진행 상황은
        &lsquo;개선요청&rsquo; 화면과 홈 위젯에서 볼 수 있습니다.
      </p>

      {/* 이름·부서 칸은 없앴다(사용자 지시 2026-09-02 · 2026-10-06). 익명이라 적어 놓고 받으면 그 말이 안 믿긴다. */}
      <Field label="요청 내용">
        <textarea
          className="input-field"
          style={{ minHeight: '180px' }}
          placeholder="불편한 점이나 오류를 구체적으로 적어 주세요. 예: 주간회의 OO현장 수금 입력 시 저장이 되지 않음"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          disabled={submit.isPending}
        />
      </Field>

      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
        현재 페이지: <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{pathname}</span>
      </div>

      {submit.isError && <Notice tone="error">{submit.error.message}</Notice>}
    </Modal>
  );
}
